import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import api, { PLATFORM_NAME, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

/** The one-tap return from a winback email: the closure is cancelled and the discount is waiting. */
export default function Winback() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/public/winback/${token}`)
      .then(({ data }) => setOffer(data))
      .catch((e) => setError(errMsg(e)));
  }, [token]);

  const accept = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/public/winback/${token}/accept`);
      setDone(data);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="winback-page" className="flex min-h-screen items-center justify-center bg-ink-900 px-4 py-16">
      <div className="w-full max-w-lg rounded-2xl border border-ink-600 bg-ink-800 p-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">{PLATFORM_NAME}</p>

        {!offer && !error && <Loader2 className="mt-6 h-5 w-5 animate-spin text-gold" />}

        {offer && !done && (
          <>
            <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-50">
              Welcome back, {offer.company_name}
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              Everything is exactly where you left it — your trips, invoices, vehicles and chauffeurs.
              Tap below and we will reopen the account with{" "}
              <span className="text-gold">{offer.percent}% off for {offer.months} months</span> waiting
              on your next subscription.
            </p>
            <Button data-testid="winback-accept" disabled={busy} onClick={accept}
              className="mt-8 h-12 w-full bg-gold text-ink-900 hover:bg-gold-light">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                : `Reopen my account at ${offer.percent}% off`}
            </Button>
          </>
        )}

        {done && (
          <>
            <CheckCircle2 className="mt-6 h-8 w-8 text-emerald-400" />
            <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-50">Your account is back</h1>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              Sign in with {done.email} and pick any monthly plan — the {done.percent}% discount applies
              automatically for {done.months} months.
            </p>
            <Link data-testid="winback-login" to="/login"
              className="mt-8 block rounded-lg bg-gold py-3 text-center font-semibold text-ink-900 hover:bg-gold-light">
              Sign in
            </Link>
          </>
        )}

        {error && <p data-testid="winback-error" className="mt-6 text-sm text-red-400">{error}</p>}
      </div>
    </div>
  );
}
